<template>
  <div ref="root" class="tvb-affix" :style="placeholder ? rootStyle : ''">
    <div :class="{ 'tvb-affix--fixed': fixed }" :style="affixStyle">
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-affix--fixed {
  position: fixed;
}
</style>

<script setup>
import { inject, computed, onMounted, ref, shallowRef, watch, watchEffect } from 'vue'
import { useElementBounding, useEventListener } from '@vueuse/core'
import { getScrollContainer } from './scroll'

const props = defineProps({
  /**
   * @description affix element zIndex value
   * */
  zIndex: {
    type: [Number, String],
    default: 99
  },
  /**
   * @description target container. (CSS selector)
   */
  target: {
    type: String,
    default: ''
  },
  /**
   * @description offset distance
   * */
  offset: {
    type: Number,
    default: 0
  },
  /**
   * @description position of affix
   * */
  position: {
    type: String,
    values: ['top', 'bottom'],
    default: 'top'
  },
  /**
   * @description 是否在标签位置生成一个等高的占位元素
   * */
  placeholder: {
    type: Boolean,
    default: true
  },
  /**
   * @description 特殊情况下拿不到宽高，则手动传入，如有绝对定位时
   * */
  rootSize: {
    type: Object,
    default: () => {
      return {}
    }
  }
})
const emit = defineEmits(['scroll', 'change'])

const _target = shallowRef()
const root = shallowRef()
const scrollContainer = shallowRef()
const windowHeight = inject('height')
const {
  height: _rootHeight,
  width: _rootWidth,
  top: rootTop,
  bottom: rootBottom,
  update: updateRoot
} = useElementBounding(root, { windowScroll: false })
const targetRect = useElementBounding(_target)
const rootHeight = computed(() => props.rootSize?.rootHeight || _rootHeight.value)
const rootWidth = computed(() => props.rootSize?.rootWidth || _rootWidth.value)

const fixed = ref(false)
const scrollTop = ref(0)
const transform = ref(0)

const rootStyle = computed(() => {
  return {
    height: fixed.value ? `${rootHeight.value}px` : '',
    width: fixed.value ? `${rootWidth.value}px` : ''
  }
})

const affixStyle = computed(() => {
  if (!fixed.value) return {}

  const offset = props.offset ? props.offset + 'px' : 0
  return {
    height: `${rootHeight.value}px`,
    width: `${rootWidth.value}px`,
    top: props.position === 'top' ? offset : '',
    bottom: props.position === 'bottom' ? offset : '',
    transform: transform.value ? `translateY(${transform.value}px)` : '',
    zIndex: props.zIndex
  }
})

const update = () => {
  if (!scrollContainer.value) return

  scrollTop.value =
    scrollContainer.value instanceof Window
      ? document.documentElement.scrollTop
      : scrollContainer.value.scrollTop || 0

  if (props.position === 'top') {
    if (props.target) {
      const difference = targetRect.bottom.value - props.offset - rootHeight.value
      fixed.value = props.offset > rootTop.value && targetRect.bottom.value > 0
      transform.value = difference < 0 ? difference : 0
    } else {
      fixed.value = props.offset > rootTop.value
    }
  } else if (props.target) {
    const difference = windowHeight.value - targetRect.top.value - props.offset - rootHeight.value
    fixed.value =
      windowHeight.value - props.offset < rootBottom.value &&
      windowHeight.value > targetRect.top.value
    transform.value = difference < 0 ? -difference : 0
  } else {
    fixed.value = windowHeight.value - props.offset < rootBottom.value
  }
}

const handleScroll = () => {
  updateRoot()
  emit('scroll', {
    scrollTop: scrollTop.value,
    fixed: fixed.value
  })
}

watch(fixed, (val) => emit('change', val))

onMounted(() => {
  if (props.target) {
    _target.value = document.querySelector(props.target) ?? undefined
    if (!_target.value) throw new Error(`TvbAffix: Target is not existed: ${props.target}`)
  } else {
    _target.value = document.documentElement
  }
  scrollContainer.value = getScrollContainer(root.value, true)
  updateRoot()
})

useEventListener(scrollContainer, 'scroll', handleScroll)
watchEffect(update)

defineExpose({
  /** @description update affix status */
  update,
  /** @description update rootRect info */
  updateRoot
})
</script>
